import { Table, TableContainer, TableFooter, TableCell, TableRow } from '@mui/material'

import TableBody from './TableBody'
import TableHead, { ITableColumn } from './TableHead'
import SkeletonRow from '../Skeleton'
export interface GenericDataTableInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: number | string | any
}

export type Order = 'asc' | 'desc'

// Opt-in row selection. Rows are tracked by id rather than by position, so a selection holds across
// paging, sorting and filtering; the header checkbox acts on the rows currently shown.
// A row whose id is empty cannot be selected and is shown without a checkbox.
export interface DataTableSelection<T> {
  getRowId: (row: T) => string
  // Names the row to assistive technology, so each checkbox says which row it picks.
  getRowLabel?: (row: T) => string
  selected: ReadonlySet<string>
  onChange: (next: Set<string>) => void
}
export interface DataTableProps<T extends GenericDataTableInterface> {
  rows: T[]
  columns: ITableColumn<T>[]
  footerContent?: React.ReactNode
  loadingState?: boolean
  onSort?: (property: keyof T, order: Order) => void
  sortBy?: keyof T
  sortOrder?: Order
  handleRowClick?: (row: T) => void
  renderNestedContent?: (row: T) => React.ReactNode
  selection?: DataTableSelection<T>
  // For a table with fixed column widths: the narrowest it is laid out at. Below it the table keeps
  // this width and scrolls sideways inside its container, rather than squeezing its headers.
  minWidth?: string
}

export function DataTable<T extends GenericDataTableInterface>({
  rows,
  columns,
  footerContent,
  loadingState,
  sortBy,
  sortOrder,
  onSort,
  handleRowClick,
  renderNestedContent,
  selection,
  minWidth,
}: DataTableProps<T>) {
  // Opt-in: a table whose columns declare widths is laid out fixed and confined
  // to its container. Tables that declare none keep the previous behaviour.
  const hasFixedColumns = columns.some(column => column.width)

  const handleRequestSort = (property: keyof T) => {
    const isAsc = sortBy === property && sortOrder === 'asc'
    const nextOrder = isAsc ? 'desc' : 'asc'
    onSort?.(property, nextOrder as Order)
  }

  return (
    <TableContainer
      sx={{
        border: '1px solid #EAECF0',
        borderRadius: '8px',
        paddingBottom: 1,
        overflowX: 'auto',
      }}
    >
      {loadingState ? (
        <SkeletonRow columns={columns} />
      ) : (
        <Table
          aria-labelledby="tableTitle"
          sx={
            hasFixedColumns
              ? { tableLayout: 'fixed', width: '100%', minWidth }
              : {
                  // Fill the container when columns are few; grow + scroll (keeping
                  // columns content-tight) when there are many. Avoids the auto-layout
                  // stretch that leaves wide, sparse columns.
                  minWidth: '100%',
                  width: 'max-content',
                }
          }
        >
          <TableHead<T>
            rowCount={rows?.length}
            columns={columns}
            order={sortOrder ?? 'asc'}
            orderBy={sortBy as string}
            onRequestSort={handleRequestSort}
            visibleRows={rows as T[]}
            showNestedContentIndicator={!!renderNestedContent}
            selection={selection}
          />
          <TableBody<T>
            visibleRows={rows as T[]}
            columns={columns}
            handleRowClick={handleRowClick}
            renderNestedContent={renderNestedContent}
            selection={selection}
          />
          {footerContent && (
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={columns.length + (renderNestedContent ? 1 : 0) + (selection ? 1 : 0)}
                  sx={{ padding: 0, borderBottom: 0 }}
                >
                  {footerContent}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      )}
    </TableContainer>
  )
}
